import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, Alert, Paper } from '@mui/material';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useTeam } from 'team/contexts/teamContext';
import { requestNotificationPermission, registerFcmTokenIfPermissionGranted } from 'team/utils/pushNotificationUtils';

const TeamSettingsList = () => {
    const { currentTeamId } = useTeam();
    const [permissionStatus, setPermissionStatus] = useState(null);
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState(null);

    useEffect(() => {
        if ('Notification' in window) {
            setPermissionStatus(Notification.permission);
        }
    }, []);

    // When permission was granted in address bar but we never saved the token, auto-register on Settings load
    useEffect(() => {
        if (!currentTeamId || permissionStatus !== 'granted') return;
        let cancelled = false;
        (async () => {
            const result = await registerFcmTokenIfPermissionGranted(currentTeamId);
            if (!cancelled && result.success) {
                setMessage({ text: 'Push notifications are now active for this device.', severity: 'success' });
            }
        })();
        return () => { cancelled = true; };
    }, [currentTeamId, permissionStatus]);

    const handleEnablePush = async () => {
        if (!currentTeamId) return;
        setLoading(true);
        setMessage(null);
        const result = await requestNotificationPermission(currentTeamId);
        setLoading(false);
        setPermissionStatus(Notification.permission);
        if (result.success) {
            setMessage({ text: 'Push notifications enabled. You will receive team notifications on this device.', severity: 'success' });
        } else if (result.reason === 'denied') {
            setMessage({
                text: 'Notifications are blocked. To enable: click the lock icon in the address bar → Site settings → set Notifications to Allow.',
                severity: 'warning'
            });
        } else if (result.reason === 'unsupported') {
            setMessage({ text: 'Push notifications are not supported in this browser.', severity: 'warning' });
        } else {
            setMessage({ text: 'Could not enable push notifications. Check Firebase configuration and try again.', severity: 'error' });
        }
    };

    const isGranted = permissionStatus === 'granted';

    return (
        <Box sx={{ maxWidth: 800 }}>
            <Paper elevation={0} sx={{ p: 4, borderRadius: 2, backgroundColor: 'white', mb: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#333', mb: 3 }}>
                    Push Notifications
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                    <Box sx={{ flex: 1, minWidth: 200 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <NotificationsActiveIcon sx={{ color: '#666', fontSize: 20 }} />
                            <Typography variant="body1" sx={{ fontWeight: 500, color: '#4f4f4f' }}>
                                Receive team notifications
                            </Typography>
                        </Box>
                        <Typography variant="body2" sx={{ color: '#828282', lineHeight: 1.5 }}>
                            Enable to receive push notifications when your team sends updates. Allow notifications when your browser prompts you.
                        </Typography>
                    </Box>
                    {isGranted ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'success.main' }}>
                            <CheckCircleIcon fontSize="small" />
                            <Typography variant="body2" fontWeight={600}>Enabled</Typography>
                        </Box>
                    ) : (
                        <Button
                            variant="contained"
                            onClick={handleEnablePush}
                            disabled={loading || !currentTeamId}
                            sx={{ textTransform: 'none', fontWeight: 600 }}
                        >
                            {loading ? 'Enabling...' : 'Enable'}
                        </Button>
                    )}
                </Box>
                {permissionStatus === 'denied' && !message && (
                    <Alert severity="info" sx={{ mt: 2 }}>
                        Notifications are blocked for this site. Enable them in your browser: click the lock icon in the address bar → Site settings → Notifications → Allow.
                    </Alert>
                )}
                {message && (
                    <Alert severity={message.severity} sx={{ mt: 2 }} onClose={() => setMessage(null)}>
                        {message.text}
                    </Alert>
                )}
            </Paper>
        </Box>
    );
};

export default TeamSettingsList;

